import React, { useState, useEffect } from 'react';
import { ResponsivePie } from '@nivo/pie';
import Header from '../components/Header';
import DataTable from '../components/DataTable';
import TimeSeriesChart from '../components/TimeSeriesChart';
import { fetchClaraAiUsage, fetchClaraUsageOverTime, fetchClaraUsagePercentage, fetchAverageClaraRating } from '../services/api';
import { createSafeDateFromString } from '../utils/dateUtils';

const ClaraUsage = () => {
    const [usageData, setUsageData] = useState([]);
    const [timeSeriesData, setTimeSeriesData] = useState([]);
    const [pieChartData, setPieChartData] = useState([]);
    const [averageRating, setAverageRating] = useState(null);
    
    // Separate loading states for each data source
    const [loadingUsage, setLoadingUsage] = useState(true);
    const [loadingTimeSeries, setLoadingTimeSeries] = useState(true);
    const [loadingPieChart, setLoadingPieChart] = useState(true);
    const [loadingRating, setLoadingRating] = useState(true);
    
    const [error, setError] = useState(null);

    useEffect(() => {
        // Fetch table data
        const loadUsageData = async () => {
            try {
                setLoadingUsage(true);
                const usageResponse = await fetchClaraAiUsage();
                const sortedData = [...usageResponse].sort((a, b) => b.number - a.number);
                setUsageData(sortedData);
            } catch (err) {
                console.error('Error loading Clara AI usage data:', err);
                setError('Failed to load usage data');
            } finally {
                setLoadingUsage(false);
            }
        };

        // Fetch time series data
        const loadTimeSeriesData = async () => {
            try {
                setLoadingTimeSeries(true);
                const timeSeriesResponse = await fetchClaraUsageOverTime();
                const transformedTimeSeries = transformTimeSeriesData(timeSeriesResponse);
                setTimeSeriesData(transformedTimeSeries);
            } catch (err) {
                console.error('Error loading time series data:', err);
            } finally {
                setLoadingTimeSeries(false);
            }
        };

        // Fetch pie chart data
        const loadPieChartData = async () => {
            try {
                setLoadingPieChart(true);
                const percentageResponse = await fetchClaraUsagePercentage();
                const transformedPieData = transformPieChartData(percentageResponse);
                setPieChartData(transformedPieData);
            } catch (err) {
                console.error('Error loading pie chart data:', err);
            } finally {
                setLoadingPieChart(false);
            }
        };

        // Fetch rating data
        const loadRatingData = async () => {
            try {
                setLoadingRating(true);
                const ratingResponse = await fetchAverageClaraRating();
                if (ratingResponse && ratingResponse.length > 0) {
                    setAverageRating(ratingResponse[0].avg);
                }
            } catch (err) {
                console.error('Error loading rating data:', err);
            } finally {
                setLoadingRating(false);
            }
        };

        // Start all fetches in parallel
        loadUsageData();
        loadTimeSeriesData();
        loadPieChartData();
        loadRatingData();
    }, []);

    // Transform time series data for TimeSeriesChart component
    const transformTimeSeriesData = (data) => {
        if (!data || data.length === 0) return [];

        // Sort data by month chronologically
        const sortedData = [...data].sort((a, b) => a.month.localeCompare(b.month));

        return [
            {
                id: '# of Clara Users/Month',
                data: sortedData.map(item => ({
                    x: createSafeDateFromString(item.month + '-01'), // Parse "2025-04" to Date object
                    y: item.num_of_clara_users
                }))
            },
            {
                id: 'Total # of Non-Competition Writers',
                data: sortedData.map(item => ({
                    x: createSafeDateFromString(item.month + '-01'), // Parse "2025-04" to Date object
                    y: item.num_of_total_non_comp_writers
                }))
            }
        ];
    };

    // Transform pie chart data for ResponsivePie component
    const transformPieChartData = (data) => {
        if (!data || data.length === 0) return [];

        // Sort by piece_num descending
        const sortedData = [...data].sort((a, b) => b.piece_num - a.piece_num);

        return sortedData.map(item => ({
            id: item.status,
            label: item.status,
            value: item.piece_num
        }));
    };

    // Define table columns
    const columns = [
        {
            key: 'response',
            label: 'Response',
            type: 'text'
        },
        {
            key: 'number',
            label: 'Number',
            type: 'number'
        },
        {
            key: 'percentage',
            label: 'Percentage',
            type: 'percentage'
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Header pageTitle="AI Survey Analysis, Clara Users" />
            
            <div className="px-6 py-6 flex flex-col gap-8">
                {/* Chart 1: Clara AI Usage Table */}
                {loadingUsage ? (
                    <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-64">
                        <div className="text-lg text-gray-600">Loading survey responses...</div>
                    </div>
                ) : error ? (
                    <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-64">
                        <div className="text-lg text-red-600">Error: {error}</div>
                    </div>
                ) : (
                    <DataTable
                        data={usageData}
                        columns={columns}
                        title="Clara Users AI Survey Responses"
                        rowsPerPage={100}
                        showVisualBars={true}
                    />
                )}

                {/* Bottom Row: Pie Chart | Scorecard | Time Series */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Chart 3: Pie Chart - % of Pieces Using Clara */}
                    {loadingPieChart ? (
                        <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-96">
                            <div className="text-gray-600">Loading pie chart...</div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            % of Pieces Using Clara (Since the 4/9/25 Soft Launch)
                        </h3>
                        <div style={{ height: '400px' }}>
                            <ResponsivePie
                                data={pieChartData}
                                margin={{ top: 20, right: 20, bottom: 60, left: 20 }}
                                innerRadius={0}
                                padAngle={0.7}
                                cornerRadius={3}
                                activeOuterRadiusOffset={8}
                                colors={['#F59E0B', '#10B981']} // Orange for "Clara Not Used", Green for "Clara Used"
                                borderWidth={1}
                                borderColor={{
                                    from: 'color',
                                    modifiers: [['darker', 0.2]]
                                }}
                                arcLinkLabelsSkipAngle={10}
                                arcLinkLabelsTextColor="#333333"
                                arcLinkLabelsThickness={2}
                                arcLinkLabelsColor={{ from: 'color' }}
                                arcLabelsSkipAngle={10}
                                arcLabelsTextColor="#ffffff"
                                arcLabel={(d) => `${((d.value / pieChartData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%`}
                                legends={[
                                    {
                                        anchor: 'bottom',
                                        direction: 'row',
                                        justify: false,
                                        translateX: 0,
                                        translateY: 50,
                                        itemsSpacing: 10,
                                        itemWidth: 100,
                                        itemHeight: 18,
                                        itemTextColor: '#333',
                                        itemDirection: 'left-to-right',
                                        itemOpacity: 1,
                                        symbolSize: 14,
                                        symbolShape: 'circle',
                                        effects: [
                                            {
                                                on: 'hover',
                                                style: {
                                                    itemTextColor: '#000'
                                                }
                                            }
                                        ]
                                    }
                                ]}
                                tooltip={({ datum }) => (
                                    <div
                                        style={{
                                            background: 'white',
                                            padding: '12px 16px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                            {datum.label}
                                        </div>
                                        <div>
                                            Pieces: <strong>{datum.value.toLocaleString()}</strong>
                                        </div>
                                        <div>
                                            Percentage: <strong>
                                                {((datum.value / pieChartData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%
                                            </strong>
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                    )}

                    {/* Scorecard: Average Clara Rating */}
                    {loadingRating ? (
                        <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center justify-center">
                            <div className="text-gray-600">Loading rating...</div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center justify-center">
                        <p className="text-sm text-gray-600 mb-4 text-center">Clara Helpfulness Rating (1-5)</p>
                        <p className="text-6xl font-bold text-gray-800">
                            {averageRating ? averageRating.toFixed(2) : '--'}
                        </p>
                    </div>
                    )}

                    {/* Chart 2: Clara Users Over Time */}
                    {loadingTimeSeries ? (
                        <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-96">
                            <div className="text-gray-600">Loading time series...</div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            # of Clara Users/Month (Since the 4/9/25 Soft Launch)
                        </h3>
                        
                        {/* Custom Legend Above Chart */}
                        <div className="flex items-center justify-start gap-6 mb-4 ml-12">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#1f77b4' }}></div>
                                <span className="text-sm text-gray-700"># of Clara Users/Month</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ff7f0e' }}></div>
                                <span className="text-sm text-gray-700">Total # of Non-Competition Writers</span>
                            </div>
                        </div>

                        <div style={{ height: '400px' }}>
                            <TimeSeriesChart
                                data={timeSeriesData}
                                title=""
                                xAxisLabel=""
                                yAxisLabel=""
                                height={400}
                                showLegend={false}
                                dateType="monthly"
                                margin={{ top: 20, right: 20, bottom: 50, left: 50 }}
                                xAxisLegendOffset={40}
                                yAxisLegendOffset={-40}
                            />
                        </div>
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClaraUsage;